import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DEPLOYABLE_MODELS,
  DeployableModel,
  SelectedModel,
  DeploymentFramework,
  ModelAbility,
  autoAssignMigProfile,
  calculateTotalSlices,
  calculateTotalParametersB,
  calculateRecommendedVolumeSize,
} from '@/mock/gpu-instance';
import { useModelSupportMatrix } from '@/service/gpu-instance/hook';
import { ModelSupportMatrixResponse, isModelSupportMatrixError } from '@/service/gpu-instance/type';
import {
  Bot,
  Database,
  Shield,
  FileText,
  Cpu,
  X,
  Layers,
  Check,
  Eye,
  Mic,
  Search,
  Loader2,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

interface ModelMultiSelectorProps {
  framework: DeploymentFramework;
  selectedModels: SelectedModel[];
  onSelectionChange: (models: SelectedModel[]) => void;
  disabled?: boolean;
  supportsMIG?: boolean;
  singleSelection?: boolean;
}

type ModelCategory = DeployableModel['category'];

const CATEGORY_OPTIONS: { value: ModelCategory; label: string }[] = [
  { value: 'llm', label: 'LLM' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'vision', label: 'Vision' },
  { value: 'speech', label: 'Speech' },
  { value: 'guardrail', label: 'Guardrail' },
  { value: 'ocr', label: 'OCR' },
];

const getCategoryIcon = (category: DeployableModel['category']) => {
  switch (category) {
    case 'llm':
      return <Bot className="h-4 w-4" />;
    case 'embedding':
      return <Database className="h-4 w-4" />;
    case 'guardrail':
      return <Shield className="h-4 w-4" />;
    case 'ocr':
      return <FileText className="h-4 w-4" />;
    case 'vision':
      return <Eye className="h-4 w-4" />;
    case 'speech':
      return <Mic className="h-4 w-4" />;
    default:
      return <Cpu className="h-4 w-4" />;
  }
};

const getCategoryColor = (category: DeployableModel['category']) => {
  switch (category) {
    case 'llm':
      return 'bg-blue-500/10 text-blue-600';
    case 'embedding':
      return 'bg-purple-500/10 text-purple-600';
    case 'guardrail':
      return 'bg-orange-500/10 text-orange-600';
    case 'ocr':
      return 'bg-green-500/10 text-green-600';
    case 'vision':
      return 'bg-cyan-500/10 text-cyan-600';
    case 'speech':
      return 'bg-pink-500/10 text-pink-600';
    default:
      return 'bg-gray-500/10 text-gray-600';
  }
};

const getAbilityBadgeStyle = (ability: ModelAbility) => {
  switch (ability) {
    case 'text':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'vision':
      return 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20';
    case 'reasoning':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'tools':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    case 'grammar':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'embedding':
      return 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20';
    case 'typhoon-ocr':
      return 'bg-pink-500/10 text-pink-600 border-pink-500/20';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  }
};

const ModelMultiSelector = ({
  framework,
  selectedModels,
  onSelectionChange,
  disabled = false,
  supportsMIG = true,
  singleSelection = false,
}: ModelMultiSelectorProps) => {
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilters, setCategoryFilters] = useState<ModelCategory[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Tab state
  const [activeTab, setActiveTab] = useState<'preset' | 'custom'>('preset');

  // Custom model state
  const [customModelId, setCustomModelId] = useState('');
  const [validatedModel, setValidatedModel] = useState<ModelSupportMatrixResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Custom model ability configuration
  const [selectedAbilities, setSelectedAbilities] = useState<ModelAbility[]>(['text']);
  const [selectedReasoningTemplate, setSelectedReasoningTemplate] = useState<string | null>(null);
  const [selectedToolParser, setSelectedToolParser] = useState<string | null>(null);

  // Validation mutation using the new support-matrix endpoint
  const supportMatrixMutation = useModelSupportMatrix();

  // Filter models by framework, search query, and category
  const filteredModels = useMemo(() => {
    let models = DEPLOYABLE_MODELS.filter((model) => model.framework === framework);

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      models = models.filter(
        (model) =>
          model.name.toLowerCase().includes(searchLower) ||
          model.description.toLowerCase().includes(searchLower) ||
          model.modelId.toLowerCase().includes(searchLower)
      );
    }

    // Apply category filter
    if (categoryFilters.length > 0) {
      models = models.filter((model) => categoryFilters.includes(model.category));
    }

    return models;
  }, [framework, debouncedSearch, categoryFilters]);

  const totalSlices = calculateTotalSlices(selectedModels);
  const remainingSlices = 7 - totalSlices;
  const slicePercentage = (totalSlices / 7) * 100;
  const totalParametersB = calculateTotalParametersB(selectedModels);
  const recommendedVolumeSize = calculateRecommendedVolumeSize(selectedModels);

  const isModelSelected = (modelId: string) => {
    return selectedModels.some((sm) => sm.model.id === modelId);
  };

  const handleToggleModel = (model: DeployableModel) => {
    if (disabled) return;

    if (isModelSelected(model.id)) {
      // Remove model
      onSelectionChange(selectedModels.filter((sm) => sm.model.id !== model.id));
    } else {
      // Add model with auto-assigned MIG profile (or dummy profile if MIG not supported)
      const migProfile = autoAssignMigProfile(model.vramRequired);

      if (supportsMIG) {
        if (!migProfile) {
          return; // Model too large for any MIG profile
        }

        // Check if adding would exceed 7 slices
        const newTotalSlices = totalSlices + migProfile.slices;
        if (newTotalSlices > 7) {
          return; // Would exceed capacity
        }
      }

      // Use migProfile if available, otherwise create a dummy profile for non-MIG mode
      const profileToUse = migProfile || { id: 'full-gpu', name: 'Full GPU', vramCapacity: model.vramRequired, slices: 0 };

      // In single selection mode, replace the selection instead of adding
      if (singleSelection) {
        onSelectionChange([{ model, migProfile: profileToUse }]);
      } else {
        onSelectionChange([...selectedModels, { model, migProfile: profileToUse }]);
      }
    }
  };

  const handleToggleCategoryFilter = (category: ModelCategory) => {
    setCategoryFilters((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  // Validate custom model and show configuration
  const handleValidateModel = useCallback(async () => {
    if (!customModelId.trim() || disabled) return;

    setValidatedModel(null);
    setValidationError(null);

    try {
      const result = await supportMatrixMutation.mutateAsync(customModelId.trim());

      if (isModelSupportMatrixError(result)) {
        setValidationError(result.error);
        return;
      }

      setValidatedModel(result);

      // Pre-select abilities based on API detection
      const detectedAbilities: ModelAbility[] = ['text'];
      if (result.supports_vision) detectedAbilities.push('vision');
      if (result.supports_reasoning) detectedAbilities.push('reasoning');
      if (result.tool_parser) detectedAbilities.push('tools');
      setSelectedAbilities(detectedAbilities);

      // Pre-select templates from API
      setSelectedReasoningTemplate(result.reasoning_template || null);
      setSelectedToolParser(result.tool_parser || null);
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Validation failed');
    }
  }, [customModelId, disabled, supportMatrixMutation]);

  // Add validated model with selected configuration
  const handleAddCustomModel = useCallback(() => {
    if (!validatedModel || !validatedModel.repo || disabled) return;

    // Determine category based on selected abilities
    const category: DeployableModel['category'] = selectedAbilities.includes('vision') ? 'vlm' : 'llm';

    // Extract model name from repo
    const modelName = validatedModel.repo.split('/').pop() || validatedModel.repo;

    // Default VRAM estimate
    const estimatedVram = 16;

    const customModel: DeployableModel = {
      id: `custom-${Date.now()}`,
      name: modelName,
      modelId: validatedModel.repo,
      description: `${validatedModel.family} model (${validatedModel.detection_source})`,
      category: category,
      vramRequired: estimatedVram,
      parametersB: 0,
      precision: 'FP16',
      framework: framework,
      isPreset: false,
      ability: selectedAbilities,
      // vLLM overrides for custom models
      toolParser: selectedAbilities.includes('tools') ? selectedToolParser || undefined : undefined,
      reasoningTemplate: selectedAbilities.includes('reasoning') ? selectedReasoningTemplate || undefined : undefined,
    };

    const migProfile = autoAssignMigProfile(estimatedVram);

    if (supportsMIG) {
      if (!migProfile) {
        setValidationError('Model requires too much VRAM for available MIG profiles');
        return;
      }

      const newTotalSlices = totalSlices + migProfile.slices;
      if (newTotalSlices > 7) {
        setValidationError('Adding this model would exceed MIG capacity');
        return;
      }
    }

    const profileToUse = migProfile || { id: 'full-gpu', name: 'Full GPU', vramCapacity: estimatedVram, slices: 0 };

    if (singleSelection) {
      onSelectionChange([{ model: customModel, migProfile: profileToUse }]);
    } else {
      onSelectionChange([...selectedModels, { model: customModel, migProfile: profileToUse }]);
    }

    // Reset form
    setCustomModelId('');
    setValidatedModel(null);
    setValidationError(null);
    setSelectedAbilities(['text']);
    setSelectedReasoningTemplate(null);
    setSelectedToolParser(null);
    setActiveTab('preset');
  }, [validatedModel, disabled, framework, supportsMIG, totalSlices, singleSelection, selectedModels, onSelectionChange, selectedAbilities]);

  // Toggle ability selection
  const handleToggleAbility = useCallback((ability: ModelAbility) => {
    if (ability === 'text') return; // Text is always required
    setSelectedAbilities(prev =>
      prev.includes(ability)
        ? prev.filter(a => a !== ability)
        : [...prev, ability]
    );
  }, []);

  const canSelectModel = (model: DeployableModel): boolean => {
    if (isModelSelected(model.id)) return true; // Can always deselect

    // If MIG is not supported, all models can be selected
    if (!supportsMIG) return true;

    const migProfile = autoAssignMigProfile(model.vramRequired);
    if (!migProfile) return false;

    return totalSlices + migProfile.slices <= 7;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            <span className="text-base font-medium">
              {singleSelection ? 'Select Model' : 'Select Models'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {singleSelection
              ? 'Choose a model to deploy'
              : supportsMIG
                ? 'Choose models to deploy with auto-assigned MIG profiles'
                : 'Choose models to deploy (MIG not supported with Triton)'}
          </p>
        </div>
        {!singleSelection && <Badge variant="secondary">Demo</Badge>}
      </div>

      {/* MIG Allocation Summary - Only show when MIG is supported */}
      {supportsMIG ? (
        <Card className={totalSlices > 7 ? 'border-red-500' : ''}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                MIG Allocation
              </span>
              <span className={`font-medium ${totalSlices > 7 ? 'text-red-500' : ''}`}>
                {totalSlices}/7 slices
              </span>
            </div>
            <Progress
              value={Math.min(slicePercentage, 100)}
              className={`h-2 ${totalSlices > 7 ? '[&>div]:bg-red-500' : ''}`}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{remainingSlices >= 0 ? `${remainingSlices} slices remaining` : 'Exceeds capacity!'}</span>
              <span>{selectedModels.length} models selected</span>
            </div>
            {selectedModels.length > 0 && (
              <div className="pt-2 border-t flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Total: {totalParametersB}B params
                </span>
                <span className="font-medium">
                  Recommended Volume: {recommendedVolumeSize}GB
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Model Selection
              </span>
              <span className="font-medium">
                {selectedModels.length} models selected
              </span>
            </div>
            {selectedModels.length > 0 && (
              <div className="pt-2 border-t flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Total: {totalParametersB}B params
                </span>
                <span className="font-medium">
                  Recommended Volume: {recommendedVolumeSize}GB
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Models */}
      {selectedModels.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Selected Models</Label>
          <div className="space-y-2">
            {selectedModels.map((sm) => (
              <div
                key={sm.model.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-primary/5 border-primary"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-md ${getCategoryColor(sm.model.category)}`}>
                    {getCategoryIcon(sm.model.category)}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{sm.model.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {sm.model.vramRequired}GB VRAM
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {supportsMIG && (
                    <>
                      <Badge variant="outline" className="text-xs font-mono">
                        {sm.migProfile.id}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {sm.migProfile.slices} slice{sm.migProfile.slices > 1 ? 's' : ''}
                      </Badge>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleToggleModel(sm.model)}
                    disabled={disabled}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab-based Model Selection */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'preset' | 'custom')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="preset">Preset Models</TabsTrigger>
          <TabsTrigger value="custom">Custom Model</TabsTrigger>
        </TabsList>

        {/* Preset Models Tab */}
        <TabsContent value="preset" className="space-y-3 mt-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search models..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              disabled={disabled}
            />
          </div>

          {/* Category Filter Chips */}
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((cat) => {
              const isActive = categoryFilters.includes(cat.value);
              const count = DEPLOYABLE_MODELS.filter(
                (m) => m.framework === framework && m.category === cat.value
              ).length;

              if (count === 0) return null;

              return (
                <Badge
                  key={cat.value}
                  variant={isActive ? 'default' : 'outline'}
                  className={`cursor-pointer transition-colors ${
                    isActive ? '' : 'hover:bg-muted'
                  }`}
                  onClick={() => handleToggleCategoryFilter(cat.value)}
                >
                  {cat.label} ({count})
                </Badge>
              );
            })}
            {categoryFilters.length > 0 && (
              <Badge
                variant="secondary"
                className="cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={() => setCategoryFilters([])}
              >
                Clear filters
              </Badge>
            )}
          </div>

          {/* Available Models Grid */}
          <div className="grid gap-2 max-h-[400px] overflow-y-auto">
            {filteredModels.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No models found matching your criteria</p>
              </div>
            ) : (
              filteredModels.map((model) => {
                const isSelected = isModelSelected(model.id);
                const canSelect = canSelectModel(model);
                const migProfile = autoAssignMigProfile(model.vramRequired);

                return (
                  <div
                    key={model.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isSelected
                        ? 'bg-primary/5 border-primary'
                        : canSelect
                          ? 'hover:bg-muted/50 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => canSelect && !disabled && handleToggleModel(model)}
                  >
                    {/* Selection indicator */}
                    <div
                      className={`flex items-center justify-center h-5 w-5 rounded border-2 transition-colors ${
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : canSelect
                            ? 'border-muted-foreground/30'
                            : 'border-muted-foreground/20'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <div className={`p-1.5 rounded-md ${getCategoryColor(model.category)}`}>
                      {getCategoryIcon(model.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{model.name}</div>
                      <div className="text-xs text-muted-foreground">{model.description}</div>
                      {/* Ability badges */}
                      {model.ability && model.ability.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {model.ability.map((ability) => (
                            <span
                              key={ability}
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${getAbilityBadgeStyle(ability)}`}
                            >
                              {ability}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                      <span>{model.vramRequired}GB</span>
                      {supportsMIG && migProfile && (
                        <>
                          <span>-</span>
                          <Badge variant="outline" className="text-xs font-mono">
                            {migProfile.id}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Custom Model Tab */}
        <TabsContent value="custom" className="space-y-4 mt-3">
          <Card>
            <CardContent className="pt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">HuggingFace Model ID</Label>
                <p className="text-xs text-muted-foreground">
                  Enter the model ID in the format: organization/model-name
                </p>
                <Input
                  placeholder="meta-llama/Llama-3.3-70B-Instruct"
                  value={customModelId}
                  onChange={(e) => {
                    setCustomModelId(e.target.value);
                    setValidatedModel(null);
                    setValidationError(null);
                  }}
                  disabled={disabled || supportMatrixMutation.isPending || !!validatedModel}
                />
              </div>

              {!validatedModel ? (
                <Button
                  onClick={handleValidateModel}
                  disabled={!customModelId.trim() || disabled || supportMatrixMutation.isPending}
                  className="w-full"
                >
                  {supportMatrixMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Verify Model
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => {
                    setValidatedModel(null);
                    setCustomModelId('');
                    setSelectedAbilities(['text']);
                    setSelectedReasoningTemplate(null);
                    setSelectedToolParser(null);
                  }}
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Change Model
                </Button>
              )}

              {/* Validation Error */}
              {validationError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">Failed to add model</p>
                    <p className="text-xs mt-1">{validationError}</p>
                  </div>
                </div>
              )}

              {/* Model Configuration */}
              {validatedModel && validatedModel.repo && (
                <div className="space-y-4 pt-2 border-t">
                  {/* Model Info */}
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{validatedModel.repo.split('/').pop()}</p>
                    <p className="text-xs text-muted-foreground font-mono">{validatedModel.repo}</p>
                    <p className="text-xs text-muted-foreground">
                      Family: <span className="font-medium">{validatedModel.family}</span>
                      {' · '}
                      Source: <span className="font-medium">{validatedModel.detection_source}</span>
                    </p>
                  </div>

                  {/* Abilities Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Enable Capabilities</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {(['text', 'vision', 'reasoning', 'tools', 'grammar', 'embedding', 'typhoon-ocr'] as ModelAbility[]).map((ability) => {
                        const isSelected = selectedAbilities.includes(ability);
                        const isDetected = ability === 'text' ||
                          (ability === 'vision' && validatedModel.supports_vision) ||
                          (ability === 'reasoning' && validatedModel.supports_reasoning) ||
                          (ability === 'tools' && !!validatedModel.tool_parser);

                        return (
                          <div
                            key={ability}
                            onClick={() => handleToggleAbility(ability)}
                            className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-primary/10 border-primary'
                                : 'hover:bg-muted'
                            } ${ability === 'text' ? 'opacity-60 cursor-not-allowed' : ''}`}
                          >
                            <div className={`flex items-center justify-center h-4 w-4 rounded border ${
                              isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'
                            }`}>
                              {isSelected && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-sm capitalize">{ability}</span>
                            {isDetected && (
                              <span className="text-[10px] text-muted-foreground ml-auto">detected</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reasoning Parser */}
                  {selectedAbilities.includes('reasoning') && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Reasoning Parser</Label>
                      <select
                        value={selectedReasoningTemplate || ''}
                        onChange={(e) => setSelectedReasoningTemplate(e.target.value || null)}
                        className="w-full h-9 px-3 rounded-md border bg-background text-sm"
                      >
                        <option value="">None</option>
                        <option value="qwen3">qwen3</option>
                        <option value="deepseek_r1">deepseek_r1</option>
                        <option value="deepseek_v3">deepseek_v3</option>
                        <option value="glm45">glm45</option>
                        <option value="granite">granite</option>
                        <option value="mistral">mistral</option>
                        <option value="olmo3">olmo3</option>
                        <option value="ernie45">ernie45</option>
                        <option value="openai_gptoss">openai_gptoss</option>
                        <option value="holo2">holo2</option>
                        <option value="hunyuan_a13b">hunyuan_a13b</option>
                        <option value="kimi_k2">kimi_k2</option>
                        <option value="minimax_m2">minimax_m2</option>
                        <option value="seed_oss">seed_oss</option>
                        <option value="step3">step3</option>
                      </select>
                      {validatedModel.reasoning_template && (
                        <p className="text-xs text-muted-foreground">
                          Detected: <span className="font-mono">{validatedModel.reasoning_template}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Tool Parser */}
                  {selectedAbilities.includes('tools') && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Tool Parser</Label>
                      <select
                        value={selectedToolParser || ''}
                        onChange={(e) => setSelectedToolParser(e.target.value || null)}
                        className="w-full h-9 px-3 rounded-md border bg-background text-sm"
                      >
                        <option value="">None</option>
                        <option value="hermes">hermes</option>
                        <option value="mistral">mistral</option>
                        <option value="llama3_json">llama3_json</option>
                        <option value="llama4_json">llama4_json</option>
                        <option value="granite">granite</option>
                        <option value="internlm">internlm</option>
                        <option value="jamba">jamba</option>
                        <option value="pythonic">pythonic</option>
                      </select>
                      {validatedModel.tool_parser && (
                        <p className="text-xs text-muted-foreground">
                          Detected: <span className="font-mono">{validatedModel.tool_parser}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Add Button */}
                  <Button
                    onClick={handleAddCustomModel}
                    disabled={disabled}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Model
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ModelMultiSelector;
